import React, { useState, useEffect, useCallback } from 'react';
import ModalePlanning from '../ModalePlanning';
import { evenementsAPI } from '../../services/api';
import {
    MODULES_EVENEMENTS, ICONES_EVENEMENTS, CODE_FILET, moduleEvenement
} from '../../services/evenementsCategories';
import {
    proposerClassement, libelleProposition, apparenceProposition, moduleDeLaProposition
} from '../../services/classementEvenements';

/**
 * Paramétrage → sous-onglet « Événements » : LES CATÉGORIES (lot 5.4 ter).
 *
 * ── CE QUE CET ÉCRAN LISTE, ET POURQUOI CE N'EST PLUS LA MÊME CHOSE ───────────
 * Au lot 5.4 bis il listait les 33 ÉVÉNEMENTS. Il mélangeait la bibliothèque et son
 * contenu : supprimer une ligne détruisait l'événement du calendrier, et le module
 * lié posé sur chaque événement obligeait à classer 33 objets CHAQUE ANNÉE.
 *
 * Il liste maintenant les ~10 CATÉGORIES — par département, indépendantes de
 * l'année, réglées une fois. Supprimer une catégorie ne supprime aucun événement :
 * ils retombent dans « Autre ». Et « Autre » n'a pas de poubelle, parce qu'un filet
 * supprimable n'est pas un filet.
 *
 * Structure reprise de la maquette (`#pane-evenements`, redessinée le 2026-08-09) :
 *   carte unique « Catégories d'événements »
 *     en-tête  : [Proposer un classement] [+ Ajouter une catégorie]
 *     corps    : phrase d'explication, puis tableau 4 colonnes
 *                Catégorie · Module lié · Utilisée par · (actions)
 *
 * ── CE QUI A DISPARU DE CET ÉCRAN, ET OÙ C'EST PASSÉ ─────────────────────────
 *  · La carte « Événements de l'année précédente » : le panneau du Calendrier a
 *    déjà ce bouton, EN MIEUX (il permet de choisir ce qu'on reprend). Rien n'a été
 *    supprimé avant d'avoir son remplacement — il était déjà là.
 *  · Le tableau des 33 événements et sa modale d'édition. Conséquence assumée :
 *    poser une image sur UN événement particulier n'a plus d'écran jusqu'au lot 5.5
 *    (écart E15). Les colonnes restent, la dérogation n'est pas perdue — c'est le
 *    réglage qui attend son interface.
 *  · La colonne « Catégorie métier », supprimée en base par la v36 : elle faisait
 *    double emploi avec la catégorie technique et n'a jamais été saisie par personne.
 */

/** Message d'erreur lisible d'une réponse serveur — jamais un objet brut à l'écran. */
const messageErreur = (err, repli) =>
    err?.response?.data?.error || err?.response?.data?.details || err?.message || repli;

const MODELE_VIDE = {
    id: null,
    libelle: '',
    icone: 'fa-calendar-day',
    couleur: '#6B7280',
    module_cle: ''
};

/** Pastille d'une catégorie : son icône sur sa couleur. */
function Visuel({ icone, couleur, taille }) {
    const style = { background: couleur + '22', color: couleur };
    if (taille) { style.width = taille; style.height = taille; }
    return (
        <span className="pl-evt-logo" style={style}>
            <i className={'fa-solid ' + icone} />
        </span>
    );
}

function ParamEvenements({
    selectedDept,
    lectureSeule,
    // Lecture seule du RÉFÉRENTIEL : elle ne se calcule pas sur le même droit que
    // le reste du Paramétrage. Les routes `/api/evenements` sont montées sous
    // `requireModule('planning')`, alors que les routes de configuration sont sous
    // `ecritureDeptAdmin`. Un enseignant qui a « modifier » sur le Planning crée
    // déjà des événements depuis le panneau du Calendrier : les lui griser ici
    // aurait interdit dans un écran ce qui est permis dans l'autre.
    lectureSeuleEvenements
}) {
    const figé = lectureSeuleEvenements ?? lectureSeule;
    const departementId = selectedDept?.id;

    const [categories, setCategories] = useState([]);
    const [chargement, setChargement] = useState(false);
    const [erreur, setErreur] = useState('');
    const [message, setMessage] = useState('');

    const [edition, setEdition] = useState(null);
    const [enregistrement, setEnregistrement] = useState(false);

    const [suppression, setSuppression] = useState(null);   // { categorie, nb_evenements, module_perdu }
    const [suppressionEnCours, setSuppressionEnCours] = useState(false);

    // Proposition de classement
    const [propositions, setPropositions] = useState(null);
    const [creations, setCreations] = useState({});          // code -> coché
    const [retenus, setRetenus] = useState({});              // id d'événement -> coché
    const [classementEnCours, setClassementEnCours] = useState(false);

    const charger = useCallback(async () => {
        if (!departementId) { setCategories([]); return; }
        setChargement(true);
        try {
            const res = await evenementsAPI.getCategories(departementId);
            setCategories(res.data.categories || []);
            setErreur('');
        } catch (err) {
            // Un échec de chargement se voit : sans ça, un référentiel vide par
            // erreur serait indiscernable d'un référentiel réellement vide.
            setErreur('Le référentiel n\'a pas pu être chargé : ' + messageErreur(err, 'erreur inconnue'));
        } finally {
            setChargement(false);
        }
    }, [departementId]);

    useEffect(() => { charger(); }, [charger]);

    // Recharger suffit : changer d'onglet démonte le Calendrier (`PlanningModule`
    // rend l'un OU l'autre), donc il relit les catégories en revenant. Pas de
    // synchronisation à tenir entre deux écrans qui ne coexistent jamais.
    const rafraichir = () => charger();

    // ── Modale d'édition ───────────────────────────────────────────────────

    const ouvrirCreation = () => {
        setMessage(''); setErreur('');
        setEdition({ ...MODELE_VIDE });
    };

    const ouvrirEdition = (cat) => {
        setMessage(''); setErreur('');
        setEdition({
            id: cat.id,
            libelle: cat.libelle || '',
            icone: cat.icone,
            couleur: cat.couleur,
            module_cle: cat.module_cle || ''
        });
    };

    const majEdition = (champs) => setEdition(e => ({ ...e, ...champs }));

    /**
     * Choisir un module reprend SON symbole et SA couleur — ceux de sa ligne dans la
     * sidebar. La continuité de la charte devient le défaut au lieu d'une consigne à
     * appliquer à la main. Les deux restent modifiables juste après, et rien n'est
     * écrit tant qu'on n'a pas cliqué sur Enregistrer : c'est ce qui distingue ce
     * geste de la repeinte automatique que la décision D1 interdit sur les
     * événements de production.
     */
    const changerModule = (cle) => {
        const mod = cle ? moduleEvenement(cle) : null;
        if (mod) majEdition({ module_cle: cle, icone: mod.icone, couleur: mod.couleur });
        else majEdition({ module_cle: '' });
    };

    const enregistrer = async () => {
        if (!edition?.libelle.trim()) return;
        setEnregistrement(true); setErreur(''); setMessage('');
        const charge = {
            libelle: edition.libelle.trim(),
            icone: edition.icone,
            couleur: edition.couleur,
            moduleCle: edition.module_cle || ''
        };
        try {
            if (edition.id === null) {
                await evenementsAPI.createCategorie({ ...charge, departementId });
                setMessage(`Catégorie « ${charge.libelle} » créée.`);
            } else {
                await evenementsAPI.updateCategorie(edition.id, { ...charge, departementId });
                setMessage(`Catégorie « ${charge.libelle} » enregistrée.`);
            }
            setEdition(null);
            await rafraichir();
        } catch (err) {
            // L'erreur reste DANS la modale : la refermer effacerait la saisie.
            setErreur("L'enregistrement a échoué : " + messageErreur(err, 'erreur inconnue'));
        } finally {
            setEnregistrement(false);
        }
    };

    // ── Suppression : on CHIFFRE avant d'écrire ────────────────────────────

    /**
     * L'impact est demandé au serveur AVANT d'ouvrir la modale, et c'est le fond du
     * sujet : on demande avant d'agir. Le chiffre du tableau date du dernier
     * chargement de l'écran ; celui-ci est frais. Et si le contrôle échoue, la
     * modale ne s'ouvre pas : ne pas savoir ce qu'on va déplacer est une raison de
     * s'arrêter, pas de continuer (même règle que le garde-fou D-a du lot 5.4).
     */
    const ouvrirSuppression = async (cat) => {
        setMessage(''); setErreur('');
        try {
            const res = await evenementsAPI.impactCategorie(cat.id, departementId);
            setSuppression({ categorie: cat, ...res.data });
        } catch (err) {
            setErreur("L'impact de la suppression n'a pas pu être calculé, la suppression est annulée : "
                + messageErreur(err, 'erreur inconnue'));
        }
    };

    const supprimer = async () => {
        if (!suppression) return;
        setSuppressionEnCours(true); setErreur(''); setMessage('');
        try {
            const res = await evenementsAPI.deleteCategorie(suppression.categorie.id, departementId);
            const n = res.data.evenements_bascules || 0;
            setMessage(
                `Catégorie « ${suppression.categorie.libelle} » supprimée`
                + (n > 0 ? ` — ${n} événement(s) sont passés en « Autre ».` : '.')
            );
            setSuppression(null);
            await rafraichir();
        } catch (err) {
            setErreur('La suppression a échoué : ' + messageErreur(err, 'erreur inconnue'));
            setSuppression(null);
        } finally {
            setSuppressionEnCours(false);
        }
    };

    // ── Proposition de classement ──────────────────────────────────────────

    const ouvrirPropositions = async () => {
        setMessage(''); setErreur('');
        if (!departementId) return;
        try {
            // On relit les événements de TOUTES les années : les catégories ne
            // dépendent pas de l'année, et ne proposer que l'année affichée
            // laisserait sans classement des événements qu'on ne verrait jamais.
            const res = await evenementsAPI.getConfig(departementId);
            const evenements = res.data.evenements || [];
            const { categoriesACreer, lignes } = proposerClassement(evenements, categories);
            setPropositions({ categoriesACreer, lignes });
            setCreations(Object.fromEntries(categoriesACreer.map(c => [c.code, c.retenu])));
            setRetenus(Object.fromEntries(lignes.map(l => [l.evenement.id, l.retenu])));
        } catch (err) {
            setErreur('Les événements n\'ont pas pu être lus : ' + messageErreur(err, 'erreur inconnue'));
        }
    };

    const appliquerClassement = async () => {
        const aCreer = propositions.categoriesACreer.filter(c => creations[c.code]);
        const aClasser = propositions.lignes.filter(
            l => retenus[l.evenement.id] && l.categorieProposee && l.categorieProposee !== l.categorieActuelle
        );

        setClassementEnCours(true); setErreur('');
        const echecs = [];
        const codesCrees = new Set();

        // 1. Les catégories d'abord — un événement ne peut pas être rangé dans une
        //    catégorie qui n'existe pas encore (le serveur le refuserait, à juste
        //    titre : c'est la clé étrangère applicative).
        for (const c of aCreer) {
            try {
                await evenementsAPI.createCategorie({
                    departementId,
                    libelle: c.libelle,
                    icone: c.icone,
                    couleur: c.couleur,
                    moduleCle: c.module || ''
                });
                codesCrees.add(c.code);
            } catch (err) {
                echecs.push(`catégorie « ${c.libelle} »`);
            }
        }

        // 2. Puis les événements. Ceux qui visaient une catégorie dont la création a
        //    échoué sont SAUTÉS et comptés : les envoyer quand même produirait un
        //    refus par événement, donc un message illisible.
        let faits = 0;
        for (const ligne of aClasser) {
            if (ligne.aCreer && !codesCrees.has(ligne.categorieProposee)) continue;
            try {
                await evenementsAPI.updateConfig(ligne.evenement.id, {
                    departementId,
                    categorie: ligne.categorieProposee
                });
                faits++;
            } catch (err) {
                echecs.push(ligne.evenement.libelle);
            }
        }

        setPropositions(null);
        setClassementEnCours(false);
        await rafraichir();

        // On dit ce qui a marché ET ce qui a échoué. Un « 26 événements classés »
        // alors que 3 ont échoué serait exactement le silence que ce module passe
        // son temps à corriger.
        if (echecs.length) {
            setErreur(
                `${codesCrees.size} catégorie(s) créée(s) et ${faits} événement(s) classé(s), `
                + `mais ${echecs.length} ont échoué : ${echecs.join(', ')}. Ils sont restés inchangés.`
            );
        } else {
            setMessage(
                `${codesCrees.size} catégorie(s) créée(s), ${faits} événement(s) classé(s).`
            );
        }
    };

    const nbACreerCoches = propositions
        ? propositions.categoriesACreer.filter(c => creations[c.code]).length : 0;
    const nbAClasserCoches = propositions
        ? propositions.lignes.filter(l => retenus[l.evenement.id]
            && l.categorieProposee && l.categorieProposee !== l.categorieActuelle).length : 0;

    // ── Rendu ──────────────────────────────────────────────────────────────

    return (
        <>
            <div className="pl-card">
                <div className="pl-card-header">
                    <span className="ttl">
                        <i className="fa-solid fa-calendar-check" /> Catégories d'événements
                    </span>
                    <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button
                            className="pl-btn pl-btn-sm"
                            onClick={ouvrirPropositions}
                            disabled={figé || !departementId}
                            title={
                                figé ? "Vous n'avez pas le droit de modifier le planning de ce département"
                                    : "Range les événements existants dans les bonnes catégories d'après leur nom, et propose de créer celles qui manquent — rien n'est écrit sans votre validation"
                            }
                        >
                            <i className="fa-solid fa-wand-magic-sparkles" /> Proposer un classement
                        </button>
                        <button
                            className="pl-btn pl-btn-sm"
                            onClick={ouvrirCreation}
                            disabled={figé || !departementId}
                            title={figé ? "Vous n'avez pas le droit de modifier le planning de ce département" : undefined}
                        >
                            <i className="fa-solid fa-plus" /> Ajouter une catégorie
                        </button>
                    </span>
                </div>
                <div className="pl-card-body">
                    <p className="pl-hint" style={{ marginBottom: 10 }}>
                        Chaque événement du calendrier appartient à une <b>catégorie</b>. Elle lui donne
                        son <b>symbole</b>, sa <b>couleur</b>, et le <b>module</b> qui en fera une
                        « étape proposée » sur l'Accueil. <b>Supprimer une catégorie ne supprime aucun
                        événement</b> : ils retombent dans « Autre ». Les catégories valent pour
                        toutes les années.
                    </p>

                    {erreur && !edition && (
                        <div className="pl-bandeau pl-bandeau-warn">
                            <i className="fa-solid fa-triangle-exclamation" /> {erreur}
                        </div>
                    )}
                    {message && (
                        <div className="pl-bandeau pl-bandeau-ok">
                            <i className="fa-solid fa-check" /> {message}
                        </div>
                    )}

                    {chargement ? (
                        <p className="pl-hint" style={{ margin: 0 }}>
                            <i className="fa-solid fa-spinner fa-spin" /> Chargement des catégories…
                        </p>
                    ) : categories.length === 0 ? (
                        <p className="pl-hint" style={{ margin: 0 }}>
                            Aucune catégorie pour ce département. Ajoutez-en une avec le bouton ci-dessus.
                        </p>
                    ) : (
                        <div className="pl-matrix-wrap">
                            <table className="pl-cfg-types">
                                <thead>
                                    <tr>
                                        <th>Catégorie</th>
                                        <th>Module lié</th>
                                        <th>Utilisée par</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {categories.map(cat => {
                                        const mod = moduleEvenement(cat.module_cle);
                                        const n = cat.nb_evenements || 0;
                                        return (
                                            <tr key={cat.id}>
                                                <td>
                                                    <Visuel icone={cat.icone} couleur={cat.couleur} />
                                                    <b style={{ marginLeft: 6 }}>{cat.libelle}</b>
                                                </td>
                                                <td>
                                                    {mod
                                                        ? <span className="pl-badge pl-badge-info">
                                                            <i className={'fa-solid ' + mod.icone} /> {mod.label}
                                                          </span>
                                                        : <span className="pl-hint">Non proposé</span>}
                                                </td>
                                                {/* Ce chiffre est ce qui rend la suppression compréhensible.
                                                    Il porte sur TOUTES LES ANNÉES, comme la suppression. */}
                                                <td>
                                                    {n === 0
                                                        ? <span className="pl-hint">aucun événement</span>
                                                        : <><b>{n}</b> événement{n > 1 ? 's' : ''}</>}
                                                </td>
                                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    <button
                                                        className="pl-btn pl-btn-sm"
                                                        onClick={() => ouvrirEdition(cat)}
                                                        disabled={figé}
                                                        title={figé ? "Vous n'avez pas le droit de modifier le planning de ce département" : undefined}
                                                    >
                                                        <i className="fa-solid fa-pen" /> Modifier
                                                    </button>
                                                    {/* « Autre » n'a pas de poubelle : c'est le filet où
                                                        retombent les orphelins. Le serveur refuse aussi sa
                                                        suppression — un écran n'est pas une sécurité. */}
                                                    {cat.code !== CODE_FILET && (
                                                        <>
                                                            {' '}
                                                            <button
                                                                className="pl-icon-btn"
                                                                onClick={() => ouvrirSuppression(cat)}
                                                                disabled={figé}
                                                                title="Supprimer"
                                                            >
                                                                <i className="fa-solid fa-trash" />
                                                            </button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* ===== Modale d'édition / création ===== */}
            {edition && (
                <ModalePlanning
                    titre={edition.id === null ? 'Ajouter une catégorie' : 'Modifier la catégorie'}
                    icone="fa-calendar-check"
                    taille="lg"
                    onFermer={() => { setEdition(null); setErreur(''); }}
                    pied={
                        <>
                            <button className="pl-btn" onClick={() => { setEdition(null); setErreur(''); }}>
                                Annuler
                            </button>
                            <button
                                className="pl-btn pl-btn-primary"
                                onClick={enregistrer}
                                disabled={enregistrement || !edition.libelle.trim()}
                                title={!edition.libelle.trim() ? 'Saisissez un nom pour continuer' : undefined}
                            >
                                {enregistrement
                                    ? <><i className="fa-solid fa-spinner fa-spin" /> Enregistrement…</>
                                    : <><i className="fa-solid fa-check" /> Enregistrer</>}
                            </button>
                        </>
                    }
                >
                    {erreur && (
                        <div className="pl-bandeau pl-bandeau-warn">
                            <i className="fa-solid fa-triangle-exclamation" /> {erreur}
                        </div>
                    )}

                    <div className="pl-cfg-field" style={{ marginBottom: 12 }}>
                        <label>Nom</label>
                        <input
                            className="pl-f"
                            autoFocus
                            maxLength={80}
                            value={edition.libelle}
                            placeholder="ex. Conseil de département"
                            onChange={e => majEdition({ libelle: e.target.value })}
                            onKeyDown={e => { if (e.key === 'Enter' && edition.libelle.trim()) enregistrer(); }}
                        />
                    </div>

                    <div className="pl-cfg-field" style={{ marginBottom: 12 }}>
                        <label>Symbole</label>
                        <div className="pl-evt-symbole">
                            <div className="pl-evt-symbole-apercu">
                                <Visuel icone={edition.icone} couleur={edition.couleur} taille={34} />
                                <span className="pl-hint" style={{ marginTop: 4 }}>Aperçu</span>
                            </div>

                            <div className="pl-evt-symbole-choix">
                                {ICONES_EVENEMENTS.map(famille => (
                                    <div key={famille.famille} className="pl-evt-icones-famille">
                                        <div className="pl-evt-icones-titre">{famille.famille}</div>
                                        <div className="pl-evt-icones">
                                            {famille.icones.map(ic => (
                                                <button
                                                    key={famille.famille + ic.classe}
                                                    type="button"
                                                    className={'pl-evt-icone' + (edition.icone === ic.classe ? ' active' : '')}
                                                    title={ic.label}
                                                    onClick={() => majEdition({ icone: ic.classe })}
                                                >
                                                    <i className={'fa-solid ' + ic.classe} />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                {/* La maquette dessine ici un bouton « Choisir une image… ».
                                    Il n'est PAS repris : décision du 2026-08-09, une catégorie
                                    porte une icône, pas une image. Écart écrit en spec (E16). */}
                                <div className="pl-evt-symbole-actions">
                                    <span className="pl-grow" />
                                    <div className="pl-cfg-field">
                                        <label>Couleur</label>
                                        <input
                                            type="color"
                                            className="pl-evt-couleur"
                                            value={edition.couleur}
                                            onChange={e => majEdition({ couleur: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="pl-cfg-field">
                        <label>Module lié</label>
                        <select
                            className="pl-f"
                            value={edition.module_cle}
                            onChange={e => changerModule(e.target.value)}
                        >
                            <option value="">— Aucun (non proposé) —</option>
                            {MODULES_EVENEMENTS.map(m => (
                                <option key={m.cle} value={m.cle}>{m.label}</option>
                            ))}
                        </select>
                        <span className="pl-hint">
                            Rattache la catégorie à un module → les dates de ses événements deviennent
                            des « étapes proposées » sur l'Accueil. « Aucun » = non proposé. Choisir un
                            module reprend son symbole et sa couleur.
                        </span>
                    </div>
                </ModalePlanning>
            )}

            {/* ===== Suppression : la modale CHIFFRE avant d'écrire ===== */}
            {suppression && (
                <ModalePlanning
                    titre="Supprimer une catégorie"
                    icone="fa-triangle-exclamation"
                    onFermer={() => setSuppression(null)}
                    pied={
                        <>
                            <button className="pl-btn" onClick={() => setSuppression(null)}>Annuler</button>
                            <button
                                className="pl-btn pl-btn-danger"
                                onClick={supprimer}
                                disabled={suppressionEnCours}
                            >
                                {suppressionEnCours
                                    ? <><i className="fa-solid fa-spinner fa-spin" /> Suppression…</>
                                    : <><i className="fa-solid fa-check" /> Supprimer et basculer en Autre</>}
                            </button>
                        </>
                    }
                >
                    Supprimer la catégorie <strong>{suppression.categorie.libelle}</strong> ?
                    {suppression.nb_evenements === 0 ? (
                        <p className="pl-hint" style={{ marginTop: 10 }}>
                            Aucun événement ne l'utilise.
                        </p>
                    ) : (
                        <p style={{ marginTop: 10 }}>
                            <strong>{suppression.nb_evenements} événement{suppression.nb_evenements > 1 ? 's' : ''}</strong>
                            {' '}l'utilise{suppression.nb_evenements > 1 ? 'nt' : ''}, toutes années confondues.
                            Ils <strong>ne seront pas supprimés</strong> : ils passeront en <strong>Autre</strong>
                            {suppression.module_perdu && <>, et perdront le rattachement au module <strong>{suppression.module_perdu}</strong></>}.
                        </p>
                    )}
                </ModalePlanning>
            )}

            {/* ===== Proposition de classement — on montre, on ne décide pas ===== */}
            {propositions && (
                <ModalePlanning
                    titre="Classement proposé"
                    icone="fa-wand-magic-sparkles"
                    taille="lg"
                    onFermer={() => setPropositions(null)}
                    pied={
                        <>
                            <button className="pl-btn" onClick={() => setPropositions(null)}>Annuler</button>
                            <button
                                className="pl-btn pl-btn-primary"
                                onClick={appliquerClassement}
                                disabled={classementEnCours || (nbACreerCoches + nbAClasserCoches === 0)}
                                title={nbACreerCoches + nbAClasserCoches === 0 ? 'Cochez au moins une ligne' : undefined}
                            >
                                {classementEnCours
                                    ? <><i className="fa-solid fa-spinner fa-spin" /> Application…</>
                                    : <><i className="fa-solid fa-check" /> Créer {nbACreerCoches} catégorie(s) et classer {nbAClasserCoches} événement(s)</>}
                            </button>
                        </>
                    }
                >
                    <p>
                        Le classement est déduit du <b>nom</b> de chaque événement. C'est une
                        <b> proposition</b> : <b>rien ne sera écrit</b> avant que vous cliquiez.
                    </p>
                    <p className="pl-hint" style={{ marginBottom: 12 }}>
                        Les événements <b>déjà rangés</b> dans une catégorie ne sont pas cochés — cette
                        proposition ne remet pas en cause ce que vous avez réglé à la main. Les
                        symboles et couleurs de vos catégories ne sont jamais touchés.
                    </p>

                    {/* ── 1. Les catégories manquantes ────────────────────────────
                        Sans elles, une partie des événements n'aurait pas de place
                        juste et retomberait en « Autre », c'est-à-dire nulle part. */}
                    {propositions.categoriesACreer.length > 0 && (
                        <>
                            <p style={{ margin: '0 0 6px', fontWeight: 700 }}>
                                Catégories à créer ({propositions.categoriesACreer.length})
                            </p>
                            <div className="pl-matrix-wrap" style={{ marginBottom: 14 }}>
                                <table className="pl-cfg-types">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 28 }} />
                                            <th>Catégorie</th>
                                            <th>Module lié</th>
                                            <th>Réclamée par</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {propositions.categoriesACreer.map(c => {
                                            const mod = moduleDeLaProposition(c.code);
                                            return (
                                                <tr key={c.code}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!creations[c.code]}
                                                            onChange={() => setCreations(v => ({ ...v, [c.code]: !v[c.code] }))}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Visuel icone={c.icone} couleur={c.couleur} />
                                                        <b style={{ marginLeft: 6 }}>{c.libelle}</b>
                                                    </td>
                                                    <td>
                                                        {mod
                                                            ? <span className="pl-badge pl-badge-info">
                                                                <i className={'fa-solid ' + mod.icone} /> {mod.label}
                                                              </span>
                                                            : <span className="pl-hint">Non proposé</span>}
                                                    </td>
                                                    <td><b>{c.nb_evenements}</b> événement{c.nb_evenements > 1 ? 's' : ''}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {/* ── 2. Les événements ─────────────────────────────────────── */}
                    <p style={{ margin: '0 0 6px', fontWeight: 700 }}>
                        Événements ({propositions.lignes.length})
                    </p>
                    <div className="pl-matrix-wrap">
                        <table className="pl-cfg-types">
                            <thead>
                                <tr>
                                    <th style={{ width: 28 }} />
                                    <th>Événement</th>
                                    <th>Ce qui changerait</th>
                                    <th>Pourquoi</th>
                                </tr>
                            </thead>
                            <tbody>
                                {propositions.lignes.map(l => {
                                    const rien = !l.categorieProposee || l.categorieProposee === l.categorieActuelle;
                                    const app = l.categorieProposee
                                        ? apparenceProposition(l.categorieProposee, categories) : null;
                                    // Une case cochée dont la catégorie ne sera pas créée
                                    // n'écrira rien : on le dit ici plutôt que de laisser
                                    // le compte final s'en expliquer après coup.
                                    const bloque = l.aCreer && !creations[l.categorieProposee];
                                    return (
                                        <tr key={l.evenement.id} style={{ opacity: rien ? 0.55 : 1 }}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={!!retenus[l.evenement.id] && !rien}
                                                    disabled={rien}
                                                    onChange={() => setRetenus(r => ({
                                                        ...r, [l.evenement.id]: !r[l.evenement.id]
                                                    }))}
                                                />
                                            </td>
                                            <td>
                                                <b>{l.evenement.libelle}</b>
                                                {l.dejaClasse && (
                                                    <span className="pl-hint" style={{ marginLeft: 6 }}>
                                                        déjà rangé dans « {libelleProposition(l.categorieActuelle, categories)} »
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {rien
                                                    ? <span className="pl-hint">rien à changer</span>
                                                    : (
                                                        <div style={{ fontSize: 11 }}>
                                                            {libelleProposition(l.categorieActuelle, categories)} →{' '}
                                                            {app && <Visuel icone={app.icone} couleur={app.couleur} />}
                                                            <b style={{ marginLeft: 4 }}>
                                                                {libelleProposition(l.categorieProposee, categories)}
                                                            </b>
                                                            {l.aCreer && (
                                                                <span className="pl-hint">
                                                                    {' '}(catégorie {bloque ? 'non cochée ci-dessus — rien ne sera écrit' : 'à créer'})
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                            </td>
                                            <td className="pl-hint">{l.pourquoi}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </ModalePlanning>
            )}
        </>
    );
}

export default ParamEvenements;
